if (!customElements.get('deferred-mp4-video')) {
  customElements.define(
    'deferred-mp4-video',
    class DeferredMp4Video extends HTMLElement {
      connectedCallback() {
        this.poster = this.querySelector('.deferred-mp4__poster');
        this.poster?.addEventListener('click', () => this.loadVideo({ autoplay: this.hasAttribute('data-autoplay-when-visible') }), {
          once: true,
        });

        if (this.hasAttribute('data-preview-first-frame')) {
          this.observeFirstFramePreview();
          return;
        }

        if (this.hasAttribute('data-autoplay-when-visible')) {
          if (this.hasAttribute('data-wait-for-interaction') && !window.Shopify?.designMode) {
            this.waitForInteraction();
          } else {
            this.observeVisibility();
          }
        }
      }

      disconnectedCallback() {
        this.cleanupInteractionListeners();
        this.visibilityObserver?.disconnect();
        this.previewObserver?.disconnect?.();
      }

      observeFirstFramePreview() {
        if (window.Shopify?.designMode || !('IntersectionObserver' in window)) {
          this.loadVideo({ preview: true });
          return;
        }

        this.previewObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.target !== this || !entry.isIntersecting) return;
              this.previewObserver?.disconnect?.();
              this.loadVideo({ preview: true });
            });
          },
          { rootMargin: '300px 0px', threshold: 0.01 },
        );

        this.previewObserver.observe(this);
      }

      waitForInteraction() {
        this.interactionEvents = ['pointerdown', 'touchstart', 'keydown', 'scroll'];
        this.startVisibilityObservation = () => {
          this.cleanupInteractionListeners();
          this.observeVisibility();
        };

        this.interactionEvents.forEach((eventName) => {
          window.addEventListener(eventName, this.startVisibilityObservation, { once: true, passive: true });
        });
      }

      cleanupInteractionListeners() {
        if (!this.interactionEvents || !this.startVisibilityObservation) return;

        this.interactionEvents.forEach((eventName) => {
          window.removeEventListener(eventName, this.startVisibilityObservation);
        });
        this.interactionEvents = null;
        this.startVisibilityObservation = null;
      }

      loadVideo({ autoplay = false, preview = false } = {}) {
        const videoUrl = this.dataset.videoUrl;
        if (!videoUrl || !this.poster) return;

        if (this.video) {
          if (!preview) this.playLoadedVideo({ autoplay });
          return;
        }

        const video = document.createElement('video');
        video.autoplay = autoplay && !preview;
        video.controls = !autoplay && !preview;
        video.loop = autoplay && this.dataset.loop === 'true';
        video.muted = autoplay || preview;
        video.playsInline = true;
        video.preload = 'metadata';

        if (autoplay) {
          video.setAttribute('autoplay', '');
          video.setAttribute('muted', '');
        }
        video.setAttribute('playsinline', '');

        const source = document.createElement('source');
        source.src = videoUrl;
        source.type = 'video/mp4';
        video.append(source);

        const captionUrl = this.dataset.captionUrl;
        if (captionUrl) {
          const track = document.createElement('track');
          track.kind = 'captions';
          track.src = captionUrl;
          track.srclang = this.dataset.captionLanguage || 'en';
          track.label = this.dataset.captionLabel || 'English';
          track.default = this.hasAttribute('data-caption-default');
          video.append(track);
        }

        this.poster.before(video);
        this.video = video;
        this.setAttribute(preview ? 'previewing' : 'loading', 'true');
        this.setAttribute('loaded', 'true');

        if (preview) {
          const revealPreview = () => {
            this.setAttribute('preview-ready', 'true');
            this.removeAttribute('previewing');
          };
          const requestFirstFrame = () => {
            try {
              video.currentTime = 0.01;
            } catch (error) {
              // Some browsers expose the first frame without allowing an early seek.
            }
          };

          video.addEventListener('loadedmetadata', requestFirstFrame, { once: true });
          video.addEventListener('loadeddata', revealPreview, { once: true });
          video.addEventListener('seeked', revealPreview, { once: true });
          video.load?.();
          return;
        }

        const revealVideo = () => {
          if (this.hasAttribute('ready')) return;

          this.setAttribute('ready', 'true');
          window.setTimeout(() => {
            this.poster?.setAttribute('hidden', '');
            this.removeAttribute('loading');
          }, 200);
        };
        const revealAfterFirstFrame = () => {
          if (typeof video.requestVideoFrameCallback === 'function') {
            video.requestVideoFrameCallback(revealVideo);
            return;
          }

          requestAnimationFrame(() => requestAnimationFrame(revealVideo));
        };

        video.addEventListener('playing', revealAfterFirstFrame, { once: true });
        video.play().catch(() => {});
      }

      playLoadedVideo({ autoplay = false } = {}) {
        const video = this.video;
        if (!video) return;

        video.autoplay = autoplay;
        video.controls = !autoplay;
        video.loop = autoplay && this.dataset.loop === 'true';
        video.muted = autoplay;
        this.setAttribute('ready', 'true');
        this.removeAttribute('previewing');
        this.removeAttribute('loading');
        this.poster?.setAttribute('hidden', '');
        video.play().catch(() => {});
      }

      observeVisibility() {
        if (window.Shopify?.designMode || !('IntersectionObserver' in window)) {
          this.loadVideo({ autoplay: true });
          return;
        }

        this.visibilityObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.target !== this) return;

              if (entry.isIntersecting) {
                if (!this.hasAttribute('loaded')) {
                  this.loadVideo({ autoplay: true });
                } else {
                  this.video?.play().catch(() => {});
                }
                return;
              }

              this.video?.pause();
            });
          },
          { rootMargin: '300px 0px', threshold: 0.1 },
        );

        this.visibilityObserver.observe(this);
      }
    },
  );
}
